/**
 * TaskSubtasksTab - Aba de subtarefas funcional
 * 
 * Replica o layout e design da TaskList.tsx com permissões granulares por fase.
 * Busca dados via RPC get_subtasks_for_list.
 * 
 * @version 3.1.0
 * RELEASE: 2026-08-21 v3.1.0 - Subtarefa herda também Cliente/Contato e Negócio da tarefa pai
 * RELEASE: 2026-08-21 v3.0.0 - Criação de subtarefa com seletor de tipo
 *   (allowTypeSelection) + selo do módulo quando a subtarefa é de outro tipo
 * RELEASE: 2026-08-10 v2.1.0 - Subtarefa herda os veículos da tarefa pai
 * RELEASE: 2026-04-06 v1.0.0 - Placeholder inicial
 * RELEASE: 2026-04-08 v2.0.0 - Implementação funcional completa
 */
import { useState, useCallback, Fragment } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ListChecks,
  MessageSquare,
  Plus,
  Eye,
  Pencil,
  Filter,
  CheckCircle2,
  PauseCircle,
  XCircle,
  RotateCcw,
  PlayCircle,
  ChevronDown,
  ChevronRight,
  CalendarIcon,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import { format, differenceInMinutes } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTaskPhasePermissions } from '@/hooks/useTaskPhasePermissions';
import { PriorityFlag, PRIORITY_LABELS } from '@/components/tasks/PriorityFlag';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
// v3.0.0 — rótulos dos módulos (Normal | Retenção | Jurídico)
import { TASK_MODULES, type TaskModuleType } from '@/hooks/useTaskModuleType';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { TaskFormDialog } from '../TaskFormDialog';
import type { TaskDetail } from '@/hooks/useTaskDetails';
import { createLogger } from '@/lib/logger';

/** Logger do módulo */
const logger = createLogger('TaskSubtasksTab');

/** Cores da barra lateral de prioridade */
const PRIORITY_BAR_COLORS: Record<string, string> = {
  urgent: 'bg-red-500',
  high: 'bg-orange-500',
  normal: 'bg-blue-500',
  low: 'bg-muted-foreground/30',
};

/** Labels de status */
const STATUS_LABELS: Record<string, string> = {
  in_progress: 'Andamento',
  paused: 'Pausada',
  completed: 'Concluída',
  cancelled: 'Cancelada',
};

/** Estilos de status — cores vibrantes sólidas (padronizado com TaskList) */
const STATUS_STYLES: Record<string, string> = {
  pending: 'inline-flex items-center rounded-full font-medium bg-slate-100 text-slate-700 hover:bg-slate-100 dark:bg-slate-800/60 dark:text-slate-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-slate-500 before:mr-1.5',
  in_progress: 'inline-flex items-center rounded-full font-medium bg-blue-50 text-blue-700 hover:bg-blue-50 dark:bg-blue-950/40 dark:text-blue-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-blue-500 before:mr-1.5',
  paused: 'inline-flex items-center rounded-full font-medium bg-amber-50 text-amber-800 hover:bg-amber-50 dark:bg-amber-950/40 dark:text-amber-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-amber-500 before:mr-1.5',
  completed: 'inline-flex items-center rounded-full font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-emerald-500 before:mr-1.5',
  cancelled: 'inline-flex items-center rounded-full font-medium bg-rose-50 text-rose-700 hover:bg-rose-50 dark:bg-rose-950/40 dark:text-rose-300 before:content-[""] before:size-1.5 before:rounded-full before:bg-rose-500 before:mr-1.5',
};

/** Tipo de item de subtarefa retornado pela RPC */
interface SubtaskItem {
  id: string;
  number: number;
  title: string;
  category_id: string;
  phase_id: string;
  parent_task_id: string | null;
  category_name: string;
  category_color: string;
  classification_name: string | null;
  classification_color: string | null;
  phase_name: string;
  phase_color: string;
  priority: string;
  status: string;
  assigned_to: string | null;
  assigned_to_name: string | null;
  assigned_to_avatar: string | null;
  due_date: string | null;
  created_at: string;
  is_overdue: boolean;
  subtasks_count: number;
  comments_count: number;
  phase_sort_order: number;
  total_category_phases: number;
  /** v3.0.0: módulo da subtarefa (pode diferir do módulo da tarefa pai) */
  task_type?: TaskModuleType | null;
}

interface TaskSubtasksTabProps {
  task: TaskDetail;
  /** Callback para abrir modal de detalhes de uma subtarefa */
  onNavigateToTask?: (taskId: string) => void;
  /**
   * v2.1.0: Veículos da tarefa pai — herdados automaticamente ao criar subtarefa.
   */
  parentVehicles?: Array<{ id: string; label: string }>;
}

/**
 * PhaseDropdown inline — replica o componente do TaskList.tsx
 */
function PhaseDropdown({ task, disabled = false }: { task: SubtaskItem; disabled?: boolean }) {
  const queryClient = useQueryClient();
  const [phases, setPhases] = useState<Array<{ id: string; name: string; color: string }>>([]);
  const [loading, setLoading] = useState(false);

  /** Busca fases da categoria ao abrir o dropdown */
  const handleOpenChange = async (open: boolean) => {
    if (!open || !task.category_id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_task_phases', {
        p_category_id: task.category_id,
      });
      if (error) throw error;
      setPhases((data || []) as Array<{ id: string; name: string; color: string }>);
    } catch (err: any) {
      logger.error('Erro ao buscar fases', err);
    } finally {
      setLoading(false);
    }
  };

  /** Altera a fase da tarefa */
  const handleChangePhase = async (phaseId: string) => {
    if (phaseId === task.phase_id) return;
    try {
      const { error } = await supabase.rpc('move_task_phase', {
        p_task_id: task.id,
        p_new_phase_id: phaseId,
      });
      if (error) throw error;
      toast.success('Fase alterada com sucesso');
      // Invalida cache da listagem de subtarefas
      queryClient.invalidateQueries({ queryKey: ['subtasks-for-list'] });
      queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
    } catch (err: any) {
      logger.error('Erro ao alterar fase', err);
      toast.error(`Erro ao alterar fase: ${err.message}`);
    }
  };

  // Se desabilitado, renderiza apenas o display sem dropdown
  if (disabled) {
    return (
      <div className="min-w-0 max-w-[140px] px-1 py-0.5">
        <span className="text-xs text-muted-foreground truncate block">
          {task.phase_name || 'Sem fase'}
        </span>
        <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-muted/50 mt-0.5">
          <div className="h-full rounded-full transition-all" style={{
            width: `${task.total_category_phases > 0 ? Math.max(10, (task.phase_sort_order / task.total_category_phases) * 100) : 10}%`,
            backgroundColor: task.phase_color
          }} />
        </div>
      </div>
    );
  }

  return (
    <DropdownMenu onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <div className="group/phase cursor-pointer hover:bg-muted/30 rounded px-1 -mx-1 py-0.5 transition-colors min-w-0 max-w-[140px]">
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground truncate block">
              {task.phase_name || 'Sem fase'}
            </span>
            <ChevronDown className="h-3 w-3 text-muted-foreground opacity-0 group-hover/phase:opacity-100 transition-opacity shrink-0" />
          </div>
          <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-muted/50 mt-0.5">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${task.total_category_phases > 0 ? Math.max(10, (task.phase_sort_order / task.total_category_phases) * 100) : 10}%`,
                backgroundColor: task.phase_color
              }}
            />
          </div>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[180px]" onClick={(e) => e.stopPropagation()}>
        {loading ? (
          <div className="px-2 py-2 text-xs text-muted-foreground">Carregando...</div>
        ) : phases.length === 0 ? (
          <div className="px-2 py-2 text-xs text-muted-foreground">Nenhuma fase encontrada</div>
        ) : (
          phases.map((phase) => (
            <DropdownMenuItem
              key={phase.id}
              onClick={() => handleChangePhase(phase.id)}
              className={cn(
                'flex items-center gap-2 text-xs',
                phase.id === task.phase_id && 'bg-accent font-medium'
              )}
            >
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: phase.color }}
              />
              {phase.name}
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function TaskSubtasksTab({ task, onNavigateToTask, parentVehicles }: TaskSubtasksTabProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  // Estados para expand/collapse de sub-subtarefas (nível 3)
  const [expandedSubs, setExpandedSubs] = useState<Set<string>>(new Set());
  const [subSubtasksMap, setSubSubtasksMap] = useState<Record<string, SubtaskItem[]>>({});
  const [loadingSubSubs, setLoadingSubSubs] = useState<Set<string>>(new Set());

  // Hook de permissões granulares por fase (da categoria da tarefa pai)
  const { canDoOnPhase } = useTaskPhasePermissions(task.category_id);

  /** Helper: verifica se o usuário logado é o responsável pela subtarefa */
  const isResponsibleFor = useCallback((sub: SubtaskItem) => {
    return sub.assigned_to === user?.id;
  }, [user?.id]);

  // Busca subtarefas via RPC
  const { data: subtasks = [], isLoading } = useQuery({
    queryKey: ['subtasks-for-list', task.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_subtasks_for_list', {
        p_task_id: task.id,
        p_tenant_hint: null,
      });
      if (error) {
        logger.error('Erro ao buscar subtarefas', error);
        throw error;
      }
      return (data || []) as SubtaskItem[];
    },
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });

  /** Abre modal de detalhes da subtarefa */
  const handleViewSubtask = (sub: SubtaskItem) => {
    if (onNavigateToTask) {
      onNavigateToTask(sub.id);
    }
  };

  /** Abre dialog de edição */
  const handleEditSubtask = (sub: SubtaskItem) => {
    setEditingTaskId(sub.id);
    setShowEditDialog(true);
  };

  /** Altera status da subtarefa via RPC */
  const handleChangeStatus = async (taskId: string, newStatus: string) => {
    try {
      const { error } = await supabase.rpc('change_task_status', {
        p_task_id: taskId,
        p_new_status: newStatus as any,
      });
      if (error) throw error;
      toast.success(`Status alterado para "${STATUS_LABELS[newStatus] || newStatus}"`);
      queryClient.invalidateQueries({ queryKey: ['subtasks-for-list', task.id] });
      queryClient.invalidateQueries({ queryKey: ['task-details', task.id] });
      queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
    } catch (err: any) {
      logger.error('Erro ao alterar status:', err);
      toast.error(err.message || 'Erro ao alterar status');
    }
  };

  /**
   * Expande/colapsa sub-subtarefas (nível 3) de uma subtarefa
   * Lazy load via RPC get_subtasks_for_list
   */
  const toggleExpandSub = async (subId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (expandedSubs.has(subId)) {
      setExpandedSubs(prev => {
        const next = new Set(prev);
        next.delete(subId);
        return next;
      });
      return;
    }
    // Buscar sub-subtarefas se não estão em cache
    if (!subSubtasksMap[subId]) {
      setLoadingSubSubs(prev => new Set(prev).add(subId));
      try {
        const { data, error } = await supabase.rpc('get_subtasks_for_list', {
          p_task_id: subId,
          p_tenant_hint: null,
        });
        if (error) throw error;
        setSubSubtasksMap(prev => ({
          ...prev,
          [subId]: (data || []) as SubtaskItem[],
        }));
      } catch (err: any) {
        logger.error('Erro ao buscar sub-subtarefas', err);
        toast.error('Erro ao carregar sub-subtarefas');
        return;
      } finally {
        setLoadingSubSubs(prev => {
          const next = new Set(prev);
          next.delete(subId);
          return next;
        });
      }
    }
    setExpandedSubs(prev => new Set(prev).add(subId));
  };

  const formatDue = (dateStr: string | null): string => {
    if (!dateStr) return '—';
    return format(new Date(dateStr), 'dd/MM/yy', { locale: ptBR });
  };

  /** Calcula informação de prazo com variante visual (overdue/warning/remaining) */
  const getDeadlineInfo = (dueDate: string | null, status: string) => {
    if (!dueDate) return null;
    if (status === 'completed' || status === 'cancelled') return null;

    const now = new Date();
    const due = new Date(dueDate);
    const diffMin = differenceInMinutes(due, now);

    // Vencido
    if (diffMin < 0) {
      const absDiff = Math.abs(diffMin);
      const days = Math.floor(absDiff / (60 * 24));
      const hours = Math.floor((absDiff % (60 * 24)) / 60);
      return {
        label: days > 0 ? `${days}d ${hours}h` : `${hours}h`,
        variant: 'overdue' as const,
        isOverdue: true,
      };
    }

    // Urgente: menos de 24h restantes
    if (diffMin < 60 * 24) {
      const hours = Math.floor(diffMin / 60);
      const mins = diffMin % 60;
      return {
        label: hours > 0 ? `${hours}h ${mins}m` : `${mins}m`,
        variant: 'warning' as const,
        isOverdue: false,
      };
    }

    // Restante: mais de 24h
    const days = Math.floor(diffMin / (60 * 24));
    const hours = Math.floor((diffMin % (60 * 24)) / 60);
    return {
      label: `${days}d ${hours}h`,
      variant: 'remaining' as const,
      isOverdue: false,
    };
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-8 w-32" />
        </div>
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  // Empty state
  if (subtasks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="p-3 rounded-full bg-muted mb-3">
          <ListChecks className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-sm font-medium mb-1">Nenhuma subtarefa</h3>
        <p className="text-xs text-muted-foreground max-w-xs mb-4">
          Subtarefas ajudam a dividir o trabalho em etapas menores e acompanhar o progresso.
        </p>
        {/* Botão "Nova Subtarefa" — condicionado por can_create */}
        {canDoOnPhase(task.phase_id, 'can_create', user?.id === task.assigned_to) && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowCreateDialog(true)}
            className="gap-1.5"
          >
            <Plus className="h-3.5 w-3.5" />
            Nova Subtarefa
          </Button>
        )}

        {/* Dialog de criação */}
        <TaskFormDialog
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
          defaultParentTaskId={task.id}
          allowTypeSelection
          defaultVehicles={parentVehicles}
          
          defaultContactId={task.contact_id ?? undefined}
          defaultDealId={task.deal_id ?? undefined}
          onSuccess={() => {
            setShowCreateDialog(false);
            queryClient.invalidateQueries({ queryKey: ['subtasks-for-list', task.id] });
            queryClient.invalidateQueries({ queryKey: ['task-details', task.id] });
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header com contador + botão nova subtarefa */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium">Subtarefas</h3>
          <Badge variant="secondary" className="text-xs">
            {subtasks.length}
          </Badge>
        </div>
        {canDoOnPhase(task.phase_id, 'can_create', user?.id === task.assigned_to) && (
          <Button
            size="sm"
            onClick={() => setShowCreateDialog(true)}
            className="h-8 text-xs gap-1.5"
          >
            <Plus className="h-3.5 w-3.5" />
            Nova Subtarefa
          </Button>
        )}
      </div>

      {/* Tabela — mesmo layout do TaskList.tsx */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-6" />
              <TableHead className="w-16 text-xs font-medium">Nº</TableHead>
              <TableHead className="text-xs font-medium">Título</TableHead>
              <TableHead className="w-16 text-center text-xs font-medium">Info</TableHead>
              <TableHead className="w-32 text-xs font-medium">Categoria</TableHead>
              <TableHead className="w-28 text-xs font-medium">Fase</TableHead>
              <TableHead className="w-24 text-xs font-medium">Prioridade</TableHead>
              <TableHead className="w-28 text-xs font-medium">Status</TableHead>
              <TableHead className="w-36 text-xs font-medium">Responsável</TableHead>
              <TableHead className="w-32 text-xs font-medium">Prazo</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {subtasks.map((sub) => (
              <Fragment key={`sub-group-${sub.id}`}>
              <TableRow
                key={sub.id}
                className="cursor-pointer hover:bg-muted/40 group"
                onClick={() => handleViewSubtask(sub)}
              >
                {/* Barra de prioridade lateral */}
                <TableCell className="px-2 py-2">
                  <div className={cn(
                    "w-1 h-8 rounded-full",
                    PRIORITY_BAR_COLORS[sub.priority] || 'bg-muted-foreground/30'
                  )} />
                </TableCell>

                {/* Número — com chevron expansível para sub-subtarefas */}
                <TableCell 
                  className={cn(
                    "font-mono text-xs text-muted-foreground py-2",
                    sub.subtasks_count > 0 && "cursor-pointer select-none hover:bg-muted/40 transition-colors"
                  )}
                  onClick={sub.subtasks_count > 0 ? (e) => toggleExpandSub(sub.id, e) : undefined}
                >
                  <div className="flex items-center gap-1">
                    {sub.subtasks_count > 0 ? (
                      <ChevronRight className={cn(
                        "h-3.5 w-3.5 shrink-0 transition-transform duration-200",
                        expandedSubs.has(sub.id) && "rotate-90",
                        loadingSubSubs.has(sub.id) && "opacity-50"
                      )} />
                    ) : (
                      <span className="w-3.5 shrink-0" />
                    )}
                    #{sub.number}
                  </div>
                </TableCell>

                {/* Título */}
                <TableCell className="py-2 max-w-[300px]">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <p className="text-sm font-medium truncate">{sub.title}</p>
                      {/* v3.0.0: selo do módulo quando a subtarefa é de outro tipo */}
                      {sub.task_type && sub.task_type !== task.task_type && (
                        <Badge variant="outline" className="shrink-0 text-[10px] px-1.5 py-0">
                          {TASK_MODULES[sub.task_type].label}
                        </Badge>
                      )}
                    </div>
                    {sub.classification_name && (
                      <p className="text-xs text-muted-foreground truncate">
                        {sub.classification_name}
                      </p>
                    )}
                  </div>
                </TableCell>


                {/* Info (subtarefas + comentários) */}
                <TableCell className="text-center py-2">
                  <div className="flex items-center justify-center gap-2">
                    {sub.subtasks_count > 0 && (
                      <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                        <ListChecks className="h-3 w-3" />
                        {sub.subtasks_count}
                      </span>
                    )}
                    {sub.comments_count > 0 && (
                      <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                        <MessageSquare className="h-3 w-3" />
                        {sub.comments_count}
                      </span>
                    )}
                  </div>
                </TableCell>

                {/* Categoria — bolinha colorida + nome */}
                <TableCell className="py-2">
                  {sub.category_name ? (
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: sub.category_color || 'hsl(var(--muted-foreground))' }}
                      />
                      <span className="text-xs text-muted-foreground truncate">{sub.category_name}</span>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground/50">-</span>
                  )}
                </TableCell>

                {/* Fase — Dropdown inline */}
                <TableCell className="py-2">
                  <PhaseDropdown task={sub} disabled={!canDoOnPhase(sub.phase_id, 'can_change_phase', isResponsibleFor(sub))} />
                </TableCell>

                {/* Prioridade */}
                <TableCell className="py-2">
                  <div className="flex items-center gap-1.5">
                    <PriorityFlag priority={sub.priority} size={12} />
                    <span className="text-xs text-muted-foreground">
                      {PRIORITY_LABELS[sub.priority] || sub.priority}
                    </span>
                  </div>
                </TableCell>

                {/* Status */}
                <TableCell className="py-2">
                  <Badge className={cn('text-[10px] h-5 px-1.5 whitespace-nowrap', STATUS_STYLES[sub.status])}>
                    {STATUS_LABELS[sub.status] || sub.status}
                  </Badge>
                </TableCell>

                {/* Responsável */}
                <TableCell className="py-2">
                  {sub.assigned_to_name ? (
                    <div className="flex items-center gap-2">
                      <SmartAvatar
                        src={sub.assigned_to_avatar}
                        name={sub.assigned_to_name}
                        size="xs"
                        use3DFallback
                      />
                      <span className="text-xs text-muted-foreground truncate max-w-[80px]">
                        {sub.assigned_to_name}
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground/50">-</span>
                  )}
                </TableCell>

                {/* Prazo — layout premium duas linhas */}
                <TableCell className="py-2">
                  {sub.due_date ? (() => {
                    const deadline = getDeadlineInfo(sub.due_date, sub.status);
                    /** Estilos por variante do badge pill */
                    const variantStyles: Record<string, string> = {
                      overdue: 'bg-red-500/15 text-red-600 dark:text-red-400',
                      warning: 'bg-yellow-500/15 text-yellow-600 dark:text-yellow-400',
                      remaining: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
                    };
                    return (
                      <div className="flex flex-col gap-1">
                        {/* Linha 1: ícone calendário + data */}
                        <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                          <CalendarIcon className="w-3.5 h-3.5 shrink-0" />
                          {formatDue(sub.due_date)}
                        </div>
                        {/* Linha 2: badge pill com status */}
                        {deadline && (
                          <div className={cn(
                            'inline-flex items-center gap-0.5 px-1 py-px rounded-full text-[10px] font-bold uppercase w-fit',
                            variantStyles[deadline.variant]
                          )}>
                            {deadline.isOverdue
                              ? <AlertTriangle className="w-3 h-3 shrink-0" />
                              : <Clock className="w-3 h-3 shrink-0" />
                            }
                            {deadline.label}
                          </div>
                        )}
                      </div>
                    );
                  })() : (
                    <span className="text-xs text-muted-foreground/50">—</span>
                  )}
                </TableCell>

                {/* Menu de Ações */}
                <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Filter className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="w-52 p-1.5 shadow-lg border-border/50"
                    >
                      {/* Ver detalhes */}
                      <DropdownMenuItem
                        onClick={() => handleViewSubtask(sub)}
                        className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer"
                      >
                        <Eye className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">Ver Detalhes</span>
                      </DropdownMenuItem>

                      {/* Editar — condicionado por permissão da fase */}
                      {canDoOnPhase(sub.phase_id, 'can_edit', isResponsibleFor(sub)) && (
                        <DropdownMenuItem
                          onClick={() => handleEditSubtask(sub)}
                          className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer"
                        >
                          <Pencil className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">Editar</span>
                        </DropdownMenuItem>
                      )}

                      <DropdownMenuSeparator className="my-1.5" />

                      {/* Ações de status — condicionadas por can_change_status */}
                      {sub.status === 'in_progress' && canDoOnPhase(sub.phase_id, 'can_change_status', isResponsibleFor(sub)) && (
                        <>
                          <DropdownMenuItem
                            onClick={() => handleChangeStatus(sub.id, 'paused')}
                            className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer text-yellow-600 dark:text-yellow-400 focus:text-yellow-600 focus:bg-yellow-50 dark:focus:bg-yellow-950/30"
                          >
                            <PauseCircle className="h-4 w-4" />
                            <span className="text-sm font-medium">Pausar</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleChangeStatus(sub.id, 'completed')}
                            className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer text-emerald-600 dark:text-emerald-400 focus:text-emerald-600 focus:bg-emerald-50 dark:focus:bg-emerald-950/30"
                          >
                            <CheckCircle2 className="h-4 w-4" />
                            <span className="text-sm font-medium">Concluir</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleChangeStatus(sub.id, 'cancelled')}
                            className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer text-red-600 dark:text-red-400 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/30 mt-1"
                          >
                            <XCircle className="h-4 w-4" />
                            <span className="text-sm font-medium">Cancelar</span>
                          </DropdownMenuItem>
                        </>
                      )}

                      {sub.status === 'paused' && canDoOnPhase(sub.phase_id, 'can_change_status', isResponsibleFor(sub)) && (
                        <>
                          <DropdownMenuItem
                            onClick={() => handleChangeStatus(sub.id, 'in_progress')}
                            className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer text-blue-600 dark:text-blue-400 focus:text-blue-600 focus:bg-blue-50 dark:focus:bg-blue-950/30"
                          >
                            <PlayCircle className="h-4 w-4" />
                            <span className="text-sm font-medium">Retomar</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleChangeStatus(sub.id, 'cancelled')}
                            className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer text-red-600 dark:text-red-400 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/30 mt-1"
                          >
                            <XCircle className="h-4 w-4" />
                            <span className="text-sm font-medium">Cancelar</span>
                          </DropdownMenuItem>
                        </>
                      )}

                      {/* Reabrir — condicionado por can_reopen */}
                      {(sub.status === 'completed' || sub.status === 'cancelled') && canDoOnPhase(sub.phase_id, 'can_reopen', isResponsibleFor(sub)) && (
                        <DropdownMenuItem
                          onClick={() => handleChangeStatus(sub.id, 'in_progress')}
                          className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer"
                        >
                          <RotateCcw className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">Reabrir Tarefa</span>
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>

              {/* === Sub-subtarefas expandidas (nível 3) === */}
              {expandedSubs.has(sub.id) && subSubtasksMap[sub.id]?.map((grandChild) => (
                <TableRow
                  key={grandChild.id}
                  className="cursor-pointer hover:bg-muted/40 group bg-muted/20"
                  onClick={() => handleViewSubtask(grandChild)}
                >
                  <TableCell className="px-2 py-1.5">
                    <div className={cn(
                      "w-1 h-6 rounded-full",
                      PRIORITY_BAR_COLORS[grandChild.priority] || 'bg-muted-foreground/30'
                    )} />
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground py-1.5">
                    <div className="flex items-center gap-0.5 pl-5">
                      <span className="text-muted-foreground/30">└</span>
                      <span className="w-3.5 shrink-0" />
                      #{grandChild.number}
                    </div>
                  </TableCell>
                  <TableCell className="py-1.5 max-w-[300px]">
                    <div className="min-w-0 pl-2">
                      <p className="text-sm truncate">{grandChild.title}</p>
                      {grandChild.classification_name && (
                        <p className="text-xs text-muted-foreground truncate">
                          {grandChild.classification_name}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center py-1.5">
                    <div className="flex items-center justify-center gap-2">
                      {grandChild.subtasks_count > 0 && (
                        <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                          <ListChecks className="h-3 w-3" />
                          {grandChild.subtasks_count}
                        </span>
                      )}
                      {grandChild.comments_count > 0 && (
                        <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                          <MessageSquare className="h-3 w-3" />
                          {grandChild.comments_count}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  {/* Categoria — bolinha colorida + nome */}
                  <TableCell className="py-1.5">
                    {grandChild.category_name ? (
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: grandChild.category_color || 'hsl(var(--muted-foreground))' }}
                        />
                        <span className="text-xs text-muted-foreground truncate">{grandChild.category_name}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground/50">-</span>
                    )}
                  </TableCell>
                  <TableCell className="py-1.5">
                    <PhaseDropdown task={grandChild} disabled={!canDoOnPhase(grandChild.phase_id, 'can_change_phase', isResponsibleFor(grandChild))} />
                  </TableCell>
                  <TableCell className="py-1.5">
                    <div className="flex items-center gap-1.5">
                      <PriorityFlag priority={grandChild.priority} size={12} />
                      <span className="text-xs text-muted-foreground">
                        {PRIORITY_LABELS[grandChild.priority] || grandChild.priority}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="py-1.5">
                    <Badge className={cn('text-[10px] h-5 px-1.5 whitespace-nowrap', STATUS_STYLES[grandChild.status])}>
                      {STATUS_LABELS[grandChild.status] || grandChild.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-1.5">
                    {grandChild.assigned_to_name ? (
                      <div className="flex items-center gap-2">
                        <SmartAvatar
                          src={grandChild.assigned_to_avatar}
                          name={grandChild.assigned_to_name}
                          size="xs"
                          use3DFallback
                        />
                        <span className="text-xs text-muted-foreground truncate max-w-[80px]">
                          {grandChild.assigned_to_name}
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground/50">-</span>
                    )}
                  </TableCell>
                  <TableCell className="py-1.5">
                    {grandChild.due_date ? (() => {
                      const deadline = getDeadlineInfo(grandChild.due_date, grandChild.status);
                      const variantStyles: Record<string, string> = {
                        overdue: 'bg-red-500/15 text-red-600 dark:text-red-400',
                        warning: 'bg-yellow-500/15 text-yellow-600 dark:text-yellow-400',
                        remaining: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
                      };
                      return (
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                            <CalendarIcon className="w-3.5 h-3.5 shrink-0" />
                            {formatDue(grandChild.due_date)}
                          </div>
                          {deadline && (
                            <div className={cn(
                              'inline-flex items-center gap-0.5 px-1 py-px rounded-full text-[10px] font-bold uppercase w-fit',
                              variantStyles[deadline.variant]
                            )}>
                              {deadline.isOverdue
                                ? <AlertTriangle className="w-3 h-3 shrink-0" />
                                : <Clock className="w-3 h-3 shrink-0" />
                              }
                              {deadline.label}
                            </div>
                          )}
                        </div>
                      );
                    })() : (
                      <span className="text-xs text-muted-foreground/50">—</span>
                    )}
                  </TableCell>
                  <TableCell className="py-1.5" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Filter className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52 p-1.5 shadow-lg border-border/50">
                        <DropdownMenuItem
                          onClick={() => handleViewSubtask(grandChild)}
                          className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer"
                        >
                          <Eye className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">Ver Detalhes</span>
                        </DropdownMenuItem>
                        {canDoOnPhase(grandChild.phase_id, 'can_edit', isResponsibleFor(grandChild)) && (
                          <DropdownMenuItem
                            onClick={() => handleEditSubtask(grandChild)}
                            className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer"
                          >
                            <Pencil className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">Editar</span>
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Dialog de criação de subtarefa */}
      <TaskFormDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        defaultParentTaskId={task.id}
        allowTypeSelection
        defaultVehicles={parentVehicles}
        defaultContactId={task.contact_id ?? undefined}
        defaultDealId={task.deal_id ?? undefined}
        onSuccess={() => {
          setShowCreateDialog(false);
          queryClient.invalidateQueries({ queryKey: ['subtasks-for-list', task.id] });
          queryClient.invalidateQueries({ queryKey: ['task-details', task.id] });
        }}
      />

      {/* Dialog de edição de subtarefa */}
      {editingTaskId && (
        <TaskFormDialog
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          editTaskId={editingTaskId}
          onSuccess={() => {
            setShowEditDialog(false);
            setEditingTaskId(null);
            queryClient.invalidateQueries({ queryKey: ['subtasks-for-list', task.id] });
            queryClient.invalidateQueries({ queryKey: ['task-details', task.id] });
          }}
        />
      )}
    </div>
  );
}
